import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, clearJwt, saveJwt } from "./api";
import { hydrateToken, getTokenSync } from "./tokenStorage";
import { captureRefFromUrl, getStoredRef, clearStoredRef } from "./refCode";

const AuthContext = createContext(null);

/**
 * Decodifica il payload di un JWT senza verifica firma. Usato SOLO come
 * fallback UI quando l'app è offline / il backend è down: ci serve per
 * capire chi l'utente DICE di essere così la dashboard non sembra
 * deautenticata. La verifica vera resta lato server (/auth/me al primo
 * retry quando la rete torna).
 */
function _decodeJwtUser(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
    const payload = JSON.parse(atob(b64 + pad));
    if (!payload?.sub && !payload?.user_id) return null;
    return {
      user_id: payload.user_id || payload.sub,
      email: payload.email,
      name: payload.name,
      // _shadow flag così il resto del codice sa che è un user "ottimistico"
      _shadow: true,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // iter v30 r130 — Capacitor iOS: il WKWebView può azzerare cookie e
    // localStorage in scenari di low-memory o background prolungato. La
    // fonte autoritativa è Capacitor Preferences (UserDefaults). hydrateToken()
    // legge da lì, popola la memoria + cookie/localStorage, così tutte le
    // chiamate successive trovano il token e l'utente NON viene buttato fuori.
    const token = await hydrateToken();
    if (!token) {
      setUser(null);
      return null;
    }
    // iter v30 r098 — User M1750: dopo Stripe Customer Portal + F5 sulla
    // pagina Profilo, l'utente veniva sbattuto fuori. Causa: la /auth/me
    // poteva fallire per timeout temporaneo del proxy (post-OOM) e il catch
    // azzerava il `user` → ProtectedRoute redirige a /login. Ora distinguiamo:
    //   • 401 = token davvero non valido → logout pulito
    //   • altro errore (network, 5xx) = NON tocchiamo `user`, riproviamo
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // Token davvero non valido: pulisci tutto e fai logout pulito
        clearJwt();
        setUser(null);
        return null;
      }
      // Errori transitori (5xx, network, timeout): NON deautenticare.
      // Riproviamo una volta dopo 1.5s, poi se fallisce di nuovo restiamo
      // sullo stato corrente (l'utente vede comunque la pagina, le altre
      // chiamate API riproveranno con lo stesso JWT).
      try {
        await new Promise((r) => setTimeout(r, 1500));
        const { data } = await api.get("/auth/me");
        setUser(data);
        return data;
      } catch (e2) {
        const status2 = e2?.response?.status;
        if (status2 === 401 || status2 === 403) {
          clearJwt();
          setUser(null);
          return null;
        }
        // Altro errore di rete persistente: lascia user com'è, l'utente
        // può continuare a usare l'app. Le chiamate successive avranno
        // ritentativi grazie al normale flow axios.
        // Se abbiamo un token in memoria e non possiamo verificarlo,
        // costruiamo uno "user shadow" minimo dal JWT payload così la UI
        // non sembra deautenticata (verrà sostituito dal vero /me al
        // prossimo retry quando la rete torna).
        const fallbackUser = _decodeJwtUser(getTokenSync());
        if (fallbackUser) setUser((prev) => prev || fallbackUser);
        return null;
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Capture `?ref=CODE` from any landing URL so the referral survives
    // through Google OAuth round-trips and the registration form.
    captureRefFromUrl();
    // If returning from OAuth callback, skip /me check; AuthCallback will handle it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      if (!cancelled) setLoading(false);
      return () => { cancelled = true; };
    }
    refresh().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [refresh]);

  // iter v30 r130 — App resume listener (Capacitor iOS/Android).
  // Quando l'utente torna in foreground dopo aver lasciato l'app in
  // background, ri-validiamo silenziosamente il token. Se Capacitor
  // Preferences ha mantenuto il token (UserDefaults è persistente) ma
  // WKWebView ha azzerato cookie/localStorage, hydrateToken() li ripopola
  // PRIMA che le chiamate API successive partano → niente deauth spuria.
  // Su web usiamo l'evento standard `visibilitychange` per parità.
  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    const onResume = () => {
      if (cancelled) return;
      // Non riportiamo loading=true: vogliamo un check silenzioso che
      // sostituisce lo user solo se cambia (es. token scaduto → null).
      refresh().catch(() => { /* errori transitori già gestiti dentro refresh */ });
    };

    (async () => {
      try {
        // Capacitor 7: App.addListener("appStateChange", { isActive })
        // scatta quando l'app va in background (isActive=false) e quando
        // torna in foreground (isActive=true). Ci serve solo quest'ultimo.
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor?.isNativePlatform?.()) {
          const { App } = await import("@capacitor/app");
          const sub = await App.addListener("appStateChange", (state) => {
            if (state?.isActive) onResume();
          });
          unsubscribe = () => { try { sub.remove(); } catch { /* no-op */ } };
          return;
        }
      } catch { /* plugin assente o non-native: fallback web */ }

      // Web fallback: re-check su visibility change (tab tornata visibile)
      // o page focus. Throttled da refresh() stesso.
      if (typeof document !== "undefined") {
        const handler = () => {
          if (document.visibilityState === "visible") onResume();
        };
        document.addEventListener("visibilitychange", handler);
        unsubscribe = () => document.removeEventListener("visibilitychange", handler);
      }
    })();

    return () => {
      cancelled = true;
      try { unsubscribe(); } catch { /* no-op */ }
    };
  }, [refresh]);

  // iter v30 r145 — Configura RevenueCat (Apple IAP) appena abbiamo l'user_id.
  // Idempotente: si auto-skipa se non siamo su iOS o se già configurato.
  // Va fatto QUI invece che a ogni mount di IAPPaywall così quando l'utente
  // apre la pagina paywall le offerings sono già pronte.
  useEffect(() => {
    if (!user?.user_id) return;
    (async () => {
      try {
        const { isIOS } = await import("@/lib/native");
        if (!isIOS) return;
        const { iapConfigure } = await import("@/lib/iap");
        await iapConfigure(user.user_id);
      } catch (e) {
        // RevenueCat non disponibile (es. preview web): silent.
      }
    })();
  }, [user?.user_id]);

  const loginJwt = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    saveJwt(data.token);
    setUser(data.user);
    return data.user;
  };

  const registerJwt = async (email, password, name) => {
    const referral_code = getStoredRef() || undefined;
    // Iter 240: attribution funnel — passa la session_id della landing (se l'utente
    // arriva da lì). Backend collega chip-cliccati → iscrizione.
    let landing_session_id;
    try {
      landing_session_id = window?.sessionStorage?.getItem("pronto.landing.sid") || undefined;
    } catch { /* SSR / privacy mode */ }
    const { data } = await api.post("/auth/register", {
      email, password, name, referral_code, landing_session_id,
    });
    saveJwt(data.token);
    setUser(data.user);
    if (referral_code) clearStoredRef();
    return data;
  };

  const logout = async () => {
    // iter v29 r168 — Su iOS native, disattiva ricezione push prima di
    // pulire JWT. Evita di ricevere notifiche dopo logout / disinstalla.
    try {
      const { nativeUnregisterPush, isNative } = await import("@/lib/native");
      if (isNative) {
        await nativeUnregisterPush();
      }
    } catch (_) { /* ignore */ }
    // iter v30 r145 — Logout anche da RevenueCat (dissocia l'app_user_id).
    try {
      const { iapLogout } = await import("@/lib/iap");
      await iapLogout();
    } catch (_) { /* ignore */ }
    try {
      await api.post("/auth/logout");
    } catch (e) { /* noop */ }
    clearJwt();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, loginJwt, registerJwt, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
